import React, { useEffect, useRef } from 'react';
import { ArrowRight } from 'lucide-react';

/**
 * LiveScanRetiredPage
 *
 * Replaces the old "Live Scan" (classic) page. Live Scan is retired —
 * this is the announcement + redirect moment for Universal Live Scan,
 * now that it auto-paginates through an entire catalog on its own.
 *
 * Drop-in usage: point the old /live-scan-classic route at this
 * component instead of the old LiveScanPage.
 */

const FLOATERS = ['FULL CATALOG', 'AUTO-PAGINATE', 'EVERY PAGE', 'NO LIMITS', 'END TO END'];

const LiveScanRetiredPage = () => {
    const canvasRef = useRef(null);

    // Confetti burst on mount
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        const dpr = window.devicePixelRatio || 1;

        const resize = () => {
            canvas.width = window.innerWidth * dpr;
            canvas.height = window.innerHeight * dpr;
            canvas.style.width = window.innerWidth + 'px';
            canvas.style.height = window.innerHeight + 'px';
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        };
        resize();
        window.addEventListener('resize', resize);

        const colors = ['#14B8A6', '#3B82F6', '#8B5CF6', '#F59E0B'];
        const count = 140;
        const particles = Array.from({ length: count }, () => ({
            x: window.innerWidth / 2 + (Math.random() - 0.5) * 200,
            y: window.innerHeight * 0.3 + (Math.random() - 0.5) * 60,
            vx: (Math.random() - 0.5) * 14,
            vy: Math.random() * -14 - 4,
            size: Math.random() * 6 + 3,
            color: colors[Math.floor(Math.random() * colors.length)],
            rotation: Math.random() * 360,
            spin: (Math.random() - 0.5) * 20,
            shape: Math.random() > 0.5 ? 'rect' : 'circle',
        }));

        let frame = 0;
        let raf;
        const gravity = 0.35;
        const drag = 0.992;

        const tick = () => {
            frame++;
            ctx.clearRect(0, 0, window.innerWidth, window.innerHeight);
            let alive = false;
            for (const p of particles) {
                p.vy += gravity;
                p.vx *= drag;
                p.x += p.vx;
                p.y += p.vy;
                p.rotation += p.spin;
                const life = 1 - frame / 130;
                if (life > 0 && p.y < window.innerHeight + 40) {
                    alive = true;
                    ctx.save();
                    ctx.globalAlpha = Math.max(life, 0);
                    ctx.translate(p.x, p.y);
                    ctx.rotate((p.rotation * Math.PI) / 180);
                    ctx.fillStyle = p.color;
                    if (p.shape === 'rect') {
                        ctx.fillRect(-p.size / 2, -p.size / 3, p.size, p.size * 0.6);
                    } else {
                        ctx.beginPath();
                        ctx.arc(0, 0, p.size / 2, 0, Math.PI * 2);
                        ctx.fill();
                    }
                    ctx.restore();
                }
            }
            if (alive && frame < 160) {
                raf = requestAnimationFrame(tick);
            } else {
                ctx.clearRect(0, 0, window.innerWidth, window.innerHeight);
            }
        };
        raf = requestAnimationFrame(tick);

        return () => {
            cancelAnimationFrame(raf);
            window.removeEventListener('resize', resize);
        };
    }, []);

    return (
        <div className="lsr-root min-h-screen relative overflow-hidden flex items-center justify-center px-4 py-16">
            <style>{`
                .lsr-root {
                    background: radial-gradient(ellipse at 50% 0%, #eef6f6 0%, #f4f8fc 45%, #f8fafc 100%);
                }
                .lsr-grid {
                    position: absolute;
                    inset: 0;
                    background-image:
                        linear-gradient(rgba(100, 130, 160, 0.08) 1px, transparent 1px),
                        linear-gradient(90deg, rgba(100, 130, 160, 0.08) 1px, transparent 1px);
                    background-size: 42px 42px;
                    mask-image: radial-gradient(ellipse at 50% 30%, black 15%, transparent 70%);
                }
                .lsr-scanline {
                    position: absolute;
                    left: 0;
                    right: 0;
                    height: 160px;
                    background: linear-gradient(180deg, transparent 0%, rgba(20, 184, 166, 0.10) 45%, rgba(59, 130, 246, 0.10) 55%, transparent 100%);
                    animation: lsr-sweep 5.5s ease-in-out infinite;
                    pointer-events: none;
                }
                @keyframes lsr-sweep {
                    0% { transform: translateY(-180px); }
                    50% { transform: translateY(105vh); }
                    100% { transform: translateY(-180px); }
                }
                @keyframes lsr-pop-in {
                    0% { opacity: 0; transform: translateY(24px) scale(0.94); }
                    60% { opacity: 1; }
                    100% { opacity: 1; transform: translateY(0) scale(1); }
                }
                .lsr-stagger {
                    opacity: 0;
                    animation: lsr-pop-in 0.75s cubic-bezier(0.16, 1, 0.3, 1) forwards;
                }
                @keyframes lsr-spin {
                    to { transform: rotate(360deg); }
                }
                @keyframes lsr-float {
                    0% { transform: translate(0, 0); opacity: 0; }
                    12% { opacity: 1; }
                    88% { opacity: 1; }
                    100% { transform: translate(var(--dx), -140px); opacity: 0; }
                }
                .lsr-floater {
                    position: absolute;
                    font-size: 11px;
                    font-weight: 700;
                    letter-spacing: 0.14em;
                    color: rgba(71, 105, 130, 0.35);
                    white-space: nowrap;
                    animation: lsr-float linear infinite;
                }
                @media (prefers-reduced-motion: reduce) {
                    .lsr-stagger { animation: none; opacity: 1; }
                    .lsr-scanline, .lsr-frame-border, .lsr-floater { animation: none; }
                }
            `}</style>

            {/* Confetti canvas */}
            <canvas ref={canvasRef} className="fixed inset-0 pointer-events-none z-50" />

            {/* Background layers */}
            <div className="lsr-grid" />
            <div className="lsr-scanline" />

            {/* Floating word particles */}
            {FLOATERS.map((word, i) => (
                <span
                    key={word}
                    className="lsr-floater"
                    style={{
                        left: `${8 + i * 20}%`,
                        bottom: '-10%',
                        '--dx': `${(i % 2 === 0 ? 1 : -1) * (30 + i * 10)}px`,
                        animationDuration: `${9 + i * 1.6}s`,
                        animationDelay: `${i * 1.1}s`,
                    }}
                >
                    {word}
                </span>
            ))}

            <div className="max-w-2xl w-full relative z-10 text-center">

                {/* Headline */}
                <h1
                    className="lsr-stagger text-4xl sm:text-5xl font-extrabold text-gray-800 leading-tight mb-10"
                    style={{ animationDelay: '0ms' }}
                >
                    Now it scans<br />the whole catalog.
                </h1>

                {/* Video frame */}
                <div
                    className="lsr-stagger relative rounded-2xl p-[3px] mb-8"
                    style={{ animationDelay: '120ms' }}
                >
                    <div className="relative rounded-2xl overflow-hidden bg-white shadow-xl" style={{ aspectRatio: '16/9' }}>
                        <iframe
                            width="100%"
                            height="100%"
                            src="https://www.youtube.com/embed/zSyMvyLhPSw"
                            title="Universal Live Scan"
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                            allowFullScreen
                            style={{ display: 'block', width: '100%', height: '100%' }}
                        />
                    </div>
                </div>

                {/* CTA */}
                <div className="lsr-stagger" style={{ animationDelay: '220ms' }}>
                    <a
                        href="/universal-live-scan"
                        className="inline-flex items-center gap-2 bg-gradient-to-r from-teal-500 to-blue-600 text-white px-7 py-3 rounded-full font-bold shadow-lg hover:shadow-xl hover:scale-105 transition-transform"
                    >
                        Enter
                        <ArrowRight size={18} />
                    </a>
                </div>
            </div>
        </div>
    );
};

export default LiveScanRetiredPage;